import React, { createContext, useContext, useState, ReactNode } from "react";
import { X } from "lucide-react";

interface ToastMessage {
  id: number;
  type: "success" | "error" | "info";
  message: string;
}

interface ToastContextType {
  showToast: (msg: string, type?: "success" | "error" | "info") => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx;
};

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const showToast = (message: string, type: "success" | "error" | "info" = "info") => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, type, message }]);

    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  };

  const remove = (id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}

      {/* Toast container */}
      <div className="fixed top-5 right-5 z-50 flex flex-col gap-2">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`
              flex items-center justify-between
              min-w-[260px] max-w-sm px-4 py-3 rounded-xl shadow-lg border
              text-white animate-slide-in-right
              ${
                toast.type === "success"
                  ? "bg-emerald-600 border-emerald-700"
                  : toast.type === "error"
                  ? "bg-rose-600 border-rose-700"
                  : "bg-slate-700 border-slate-800"
              }
            `}
          >
            <span className="text-sm font-medium">{toast.message}</span>
            <button onClick={() => remove(toast.id)}>
              <X className="w-4 h-4 text-white opacity-80 hover:opacity-100" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};
